import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Text } from 'react-native-paper';
import { COLORS } from '../../constants';

/**
 * SummaryCard Component
 * 
 * A reusable component that displays a summary card containing an icon, a heading, and a description.
 * This component is designed to be flexible with various icon sizes and colors, allowing integration
 * within different parts of an application where summarized content is needed.
 * 
 * Props:
 *  - heading: String that represents the main title of the card.
 *  - description: Additional details or description shown below the heading.
 *  - icon: FontAwesome icon representation to be displayed.
 *  - size: Size of the FontAwesome icon.
 *  - iconColor: Color of the FontAwesome icon, defaults to primary color if not specified.
 *  - FontAwesomeIcon: FontAwesomeIcon component imported to be used for rendering the icon.
 *
 * Usage:
 * Can be used in any part of an application where you need to show a concise summary
 * with an icon that provides a visual cue related to the content.
 */
const SummaryCard = ({ heading, description, icon, size, iconColor, FontAwesomeIcon }) => {
    const effectiveIconColor = iconColor || COLORS.primary;

    return (
        <View style={styles.container}>
            <View style={[styles.iconContainer, { borderColor: effectiveIconColor }]}>
                <FontAwesomeIcon
                    icon={icon}
                    size={size}
                    color={effectiveIconColor}
                />
            </View>
            <View style={styles.textContainer}>
                <Text style={styles.heading}>{heading}</Text>
                <Text style={styles.description}>{description}</Text>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        paddingVertical: 16,
        paddingEnd: 4,
        backgroundColor: COLORS.background,
        borderRadius: 20,
        alignItems: 'center',
    },
    iconContainer: {
        padding: 10,
        backgroundColor: COLORS.textLight,
        borderRadius: 30,
        marginRight: 16,
        width: 52,
        height: 52,
        justifyContent: 'center',
        borderWidth: 1.5,
        alignItems: 'center',
        elevation: 4, 
        shadowColor: '#000', 
        shadowOffset: { width: 0, height: 2 }, 
        shadowOpacity: 0.2, 
        shadowRadius: 3.84, 
    },
    textContainer: {
        flexDirection: 'column',
        flex: 1,
    },
    heading: {
        color: COLORS.textDark,
        fontSize: 18,
        fontWeight: Platform.OS == 'web' ? 500 : 'bold',
        lineHeight: 26
    },
    description: {
        color: COLORS.border,
        fontSize: 16,
        paddingTop: 4,
        lineHeight: 26,
        flexWrap: 'wrap',
    }
});

export default SummaryCard;
